import { HTMLTemplateResult, TemplateResult, nothing } from 'lit';
import { JhaAdvancedTableFilterType } from './jha-advanced-table.types';

export const AllViewId = Symbol('TM_TABLE_ALL_VIEW');
export const NullViewId = Symbol('TM_TABLE_NULL_VIEW');
export const SingleViewId = Symbol('TM_TABLE_SINGLE_VIEW');

export type DataType =
    | 'badge'
    | 'currency'
    | 'currency-debit'
    | 'number'
    | 'date'
    | 'date-pretty'
    | 'date-time'
    | 'phone'
    | 'string';

/**
 * Terminal value used to indicate pagination should be disabled.
 */
export const NoPagination = Symbol('TM_TABLE_NO_PAGING');

/**
 * The context under which a data reducer is called.
 */
export enum TmTableReduceEvent {
    BulkAction = 'bulk-action',
    PageChange = 'page-change',
    Search = 'search',
    /**
     * The initial table hydration event.
     * All reducers are called on the entire dataset.
     */
    Hydrate = 'hydrate',
}

/**
 * Represents configuration for the data view(s) of the table.
 * Properties specified here are used to control individual view behavior.
 *
 * Supports single and multi-view tables, depending on what is specified.
 */

export type TmTableOptions<T = unknown> = TmTableConfig<T> | TmTableConfigMulti<T>;

export type TmTableReducer<T> = (
    event: TmTableReduceEvent,
    incoming: T[],
    current?: T[],
    query?: string
) => T[] | void;

export interface TmTablePaginationConfig<T> {
    /**
     * The page limit options a user can select from in the pagination UI.
     */
    pageLimits?: number[];
    /**
     * Callback that is invoked any time a query or page change occurs.
     *
     * This function is used to request data for the current page and/or query.
     * Its result is merged into the total items available to the table.
     *
     * @param query The current query the user has typed into the table (if any).
     * @param page The current page number in the result set.
     * @returns New items used to populate the table, if any.
     */
    getPage?: (page: number, pageSize: number, query?: string) => Promise<T[]> | T[] | void;
    /**
     * Minimum number of characters required in order for `getPage()` to be called.
     */
    minQueryLength?: number;
}

/**
 * A definition for a conventional table.
 *
 * All views of the data use this format as a basis.
 */
export interface TmTableConfig<T = unknown> {
    /**
     * The subtitle displayed below the main table title.
     */
    subtitle?: string;
    /**
     * Placeholder text to be used in the table search bar.
     */
    searchPlaceholder?: string;
    /**
     * The text to be shown when no content is available
     * or a function that produces it.
     */
    noContentMessage?: string | ((searchQuery: string) => string | TemplateResult);
    /**
     * If `true`, UI for selecting multiple/all rows is displayed.
     */
    allowMultiSelect?: boolean;
    columns?: TmTableColsDef<T>;
    /**
     * A search string used to pre-populate the table.
     */
    defaultQuery?: string;
    filters?: TmTableFilter<T>[];
    /**
     * Action UI shown when two or more items are selected.
     *
     * If a list of actions is provided, those actions will be shown as buttons. Otherwise,
     * a custom renderer can be provided to display any content desired.
     */
    selectActions?: TmTableSelectAction<T>[] | TmTableSelectCustomDef<T>;
    /**
     * The context menu actions available to each row under the '...' menu.
     */
    rowActions?: TmTableRowAction<T>[];
    /**
     * Callback that is invoked any time table state might be updated.
     *
     * This could occur for several reasons:
     * * The table was first hydrated with an initial dataset.
     * * A bulk select event occurred.
     * * A page was requested.
     *
     * Acts a reducer that resolves a list of incoming data
     * with what is already there after some event occurs to produce a new table state.
     *
     * The function should be pure and idempotent.
     * It should *not* mutate the `incoming` items array.
     *
     * This is also an appropriate place to perform transforms on the dataset
     * such as deduplication.
     *
     * @param current The items currently displayed in the view, if any.
     *
     * @param incoming Incoming data. This could be:
     * * The total set of data shared items across all views.
     * * The items currently in the view.
     *
     * @param query The current query the user has typed into the table (if any).
     *
     * @returns A new list of items that should populate the current view.
     */
    reduceData?: TmTableReducer<T>;
    /**
     * Function called when the refresh button is clicked.
     */
    refreshData?: () => Promise<T[]> | void;
    tableActions?: TmTableAction<T>[];
    pagination?: TmTablePaginationConfig<T> | typeof NoPagination;
    /**
     * If `true`, the last clicked row becomes marked as the active one.
     *
     * This is distinct from the concept used by `selectActions`.
     */
    allowActiveRow?: boolean;
    /**
     * If specified, the provided item will be marked as active when the table is first rendered.
     * Specifying this will also enable `allowActiveRow`.
     */
    initialActive?: T;
    /**
     * Specifies the starting sorting configuration for the table.
     *
     * When provided, the table will initially sort its data according to the given sort field and direction.
     *
     * The sort configuration should reference a column key and a sort direction (ascending or descending).
     *
     * Example:
     * ```
     * defaultSort: {
     *   prop: 'createdDate',
     *   direction: 'desc'
     * }
     * ```
     *
     * @remarks
     * - If not specified,
     *      then
     *   If there are no sortable columns on the table no starting sort will be applied.
     *   If there are sortable columns but no `defaultSort` is provided, the table will default to sorting by the first sortable column in ascending order.
     *
     * - The sort key must match one of the prop keys defined at `columns.definitions[].prop`.
     * - The sort direction should be either `'asc'` for ascending or `'desc'` for descending.
     */
    initialSort?: InitialSort<T>;
}

export interface InitialSort<T> {
    /**
     * The property of the data items to sort by.
     */
    prop: keyof T;
    /**
     * The sort direction.
     */
    direction: 'asc' | 'desc';
}

/**
 * An action available to an entire table that doesn't operate
 * on a specific row or selected items.
 */
export interface TmTableAction<T> {
    /**
     * An icon identifier matching one of the SVG file names defined
     * in the [JH UI repository](https://github.com/Banno/design-system/tree/next/packages/jh-icons/icons-svg).
     */
    icon?: string;
    /**
     * Optional text to display next to a label.
     */
    label?: string;
    /**
     * Reminder text to be used to supplement the icon.
     * If omitted, the label will be used when present.
     */
    tooltip?: string;
    execute: (items: T[]) => void;
}

export interface TmTableFilter<T> {
    prop: keyof T;
    type: JhaAdvancedTableFilterType;
}

/**
 * Represents a narrowed view of the table's total dataset.
 */
export interface TmTableView<T> extends TmTableConfig<T> {
    /**
     * The label displayed in the tabbed view selection UI.
     */
    label: string | ((items?: T[]) => string);
    onClick?: (data: TableClickPayload<T>) => void;
    renderSubheader?: () => TemplateResult | typeof nothing;
}

/**
 * A definition for a table containing multiple views.
 */
export interface TmTableConfigMulti<T = unknown> {
    /**
     * If enabled, displays an "All" button in the view tabbed UI that
     * populates the table with data from every view.
     */
    displayAllView?: boolean;
    views: TmTableView<T>[];
}

export interface TmTableSelectAction<T> {
    /**
     * The text displayed on the UI element for the multi-elect action.
     */
    label: string;
    /**
     * An icon identifier matching one of the SVG file names defined
     * in the [JH UI repository](https://github.com/Banno/design-system/tree/next/packages/jh-icons/icons-svg).
     */
    icon?: string;
    /**
     * The action to be executed on a set of selected items.
     *
     * Any returned items will become the new items for the current view.
     *
     * @param selected The currently selected items.
     * @returns An array of items to add to the current view.
     * If no items are returned, the function is purely used for side effects.
     */
    execute: (selected: T[]) => void | Promise<void | T[]>;
}

/**
 * Specify a custom button label and UI renderer for multi-select actions.
 */
export type TmTableSelectCustomDef<T> = {
    label: string;
    /**
     * Render function used for specifying custom content in the multi-select action area.
     *
     * @param selectedItems The currently selected items in the table.
     * @param close A function to dismiss the UI and optionally provide a list of items that should be displayed after the action is complete.
     * Any items passed will be used to update the table after the UI is closed.
     */
    render(selectedItems: T[], close: (newData?: T[]) => void): HTMLTemplateResult | string;
};

export interface TmTableColsDef<T> {
    definitions: TmTableColumn<T>[];
    canEdit?: boolean;
}

export interface TmTableColumn<T, Prop extends keyof T = keyof T> {
    prop: Prop;
    label?: string;
    sortable?: boolean;
    searchable?: boolean;
    alignment?: 'left' | 'right' | 'center';
    render?: (value: T[Prop], item: T) => HTMLTemplateResult | typeof nothing | string;
    /**
     * Data type hint for the column, which affects how values are sorted and formatted.
     * Setting this to 'number' will enable numeric sorting instead of alphabetical.
     */
    dataType?: DataType;
}

export enum TmTableEvent {
    Click = 'row-click',
    Select = 'item-select',
    Paginate = 'paginate',
    ActiveItemChange = 'active-item-changed',
}

export type TmTableClickEvent<T> = CustomEvent<TableClickPayload<T>>;
export type TmTableItemSelectEvent<T> = CustomEvent<ItemSelectPayload<T>>;
export type TmTablePagingEvent<T> = CustomEvent<TablePaginationPayload<T>>;
export type TmTableActiveItemChangeEvent<T> = CustomEvent<T | undefined>;

export type TableEventPayload<T> = {
    [TmTableEvent.Click]: TableClickPayload<T>;
    [TmTableEvent.Select]: ItemSelectPayload<T>;
    [TmTableEvent.Paginate]: TablePaginationPayload<T>;
    [TmTableEvent.ActiveItemChange]: T | undefined;
};

export interface TableClickPayload<T> {
    type: 'single' | 'double';
    item: T;
    columnKey: keyof T;
}

export interface TablePaginationPayload<T> {
    direction: 'forward' | 'backward';
    pageNumber: number;
    items: T[];
}
interface ItemSelectPayload<T> {
    items: T[];
}

export interface TmTableRowAction<T> {
    label: string;
    isHidden?: boolean | ((item: T) => boolean);
    isDisabled?: boolean | ((item: T) => boolean);
    execute: (item: T) => void;
    /**
     * An icon identifier matching one of the SVG file names defined
     * in the [JH UI repository](https://github.com/Banno/design-system/tree/next/packages/jh-icons/icons-svg).
     */
    icon?: string;
}
